import { type ConstructionCategory, InputFlag } from '@dreamgame/shared';
import { toggleFightMode } from '../state/combat';
import {
  CATEGORY_HOTKEYS,
  cancelPlacement,
  isConstructionPanelOpen,
  isPlacingConstruction,
  selectConstructionByHotkey,
  setActiveCategory,
  toggleConstructionPanel,
} from '../state/construction';
import { handleDebugInput } from '../state/debug';
import {
  cancelRespawnConfirmation,
  closeEscMenu,
  isEscMenuOpen,
  isRespawnConfirming,
  openEscMenu,
} from '../state/escMenu';
import { KeyBindings } from './keybindings';

// Regex for matching letter hotkeys (at top level for performance)
const LETTER_REGEX = /[A-Za-z]/;
const DEBUG_DIGIT_BY_CODE: Readonly<Record<string, string>> = {
  Digit0: '0',
  Digit1: '1',
  Digit2: '2',
  Digit3: '3',
  Digit4: '4',
  Digit5: '5',
  Digit6: '6',
  Digit7: '7',
  Digit8: '8',
  Digit9: '9',
};

export interface InputState {
  flags: number;
  mouseX: number;
  mouseY: number;
  mouseWorldX: number;
  mouseWorldY: number;
  mouseDown: boolean;
  rightMouseDown: boolean;
  middleMouseDown: boolean;
  mouseDeltaX: number;
  mouseDeltaY: number;
}

export interface WheelDelta {
  deltaY: number;
  mouseX: number;
  mouseY: number;
}

export class InputManager {
  private keys: Set<string> = new Set();
  private mouseX = 0;
  private mouseY = 0;
  private prevMouseX = 0;
  private prevMouseY = 0;
  private mouseDown = false;
  private rightMouseDown = false;
  private middleMouseDown = false;
  private canvas: HTMLCanvasElement | null = null;
  private wheelDeltaQueue: WheelDelta[] = [];

  // PERFORMANCE: Preallocated state object to avoid GC pressure every frame
  private readonly _cachedState: InputState = {
    flags: 0,
    mouseX: 0,
    mouseY: 0,
    mouseWorldX: 0,
    mouseWorldY: 0,
    mouseDown: false,
    rightMouseDown: false,
    middleMouseDown: false,
    mouseDeltaX: 0,
    mouseDeltaY: 0,
  };

  init({ canvas }: { canvas: HTMLCanvasElement }): void {
    this.canvas = canvas;

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    canvas.addEventListener('mousemove', this.handleMouseMove);
    canvas.addEventListener('mousedown', this.handleMouseDown);
    canvas.addEventListener('mouseup', this.handleMouseUp);
    canvas.addEventListener('contextmenu', this.handleContextMenu);
    canvas.addEventListener('wheel', this.handleWheel, { passive: false });
  }

  destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);

    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this.handleMouseMove);
      this.canvas.removeEventListener('mousedown', this.handleMouseDown);
      this.canvas.removeEventListener('mouseup', this.handleMouseUp);
      this.canvas.removeEventListener('contextmenu', this.handleContextMenu);
      this.canvas.removeEventListener('wheel', this.handleWheel);
    }
  }

  // PERFORMANCE: Returns current queue and resets length instead of allocating new array
  consumeWheelDeltas(): readonly WheelDelta[] {
    if (this.wheelDeltaQueue.length === 0) {
      return this.wheelDeltaQueue;
    }
    const deltas = this.wheelDeltaQueue;
    this.wheelDeltaQueue = [];
    return deltas;
  }

  // PERFORMANCE: Returns cached object - caller can mutate mouseWorldX/Y
  // but must not store reference long-term
  getState(): InputState {
    let flags = InputFlag.NONE;

    if (this.keys.has(KeyBindings.CAMERA_UP) || this.keys.has(KeyBindings.CAMERA_UP_ALT)) {
      flags |= InputFlag.MOVE_UP;
    }
    if (this.keys.has(KeyBindings.CAMERA_DOWN) || this.keys.has(KeyBindings.CAMERA_DOWN_ALT)) {
      flags |= InputFlag.MOVE_DOWN;
    }
    if (this.keys.has(KeyBindings.CAMERA_LEFT) || this.keys.has(KeyBindings.CAMERA_LEFT_ALT)) {
      flags |= InputFlag.MOVE_LEFT;
    }
    if (this.keys.has(KeyBindings.CAMERA_RIGHT) || this.keys.has(KeyBindings.CAMERA_RIGHT_ALT)) {
      flags |= InputFlag.MOVE_RIGHT;
    }
    if (this.mouseDown) {
      flags |= InputFlag.PRIMARY_ACTION;
    }
    if (this.rightMouseDown) {
      flags |= InputFlag.SECONDARY_ACTION;
    }
    if (this.keys.has(KeyBindings.MODIFIER) || this.keys.has(KeyBindings.MODIFIER_ALT)) {
      flags |= InputFlag.MODIFIER;
    }

    // Calculate mouse delta and update previous position
    const deltaX = this.mouseX - this.prevMouseX;
    const deltaY = this.mouseY - this.prevMouseY;
    this.prevMouseX = this.mouseX;
    this.prevMouseY = this.mouseY;

    // Update cached state instead of allocating new object
    this._cachedState.flags = flags;
    this._cachedState.mouseX = this.mouseX;
    this._cachedState.mouseY = this.mouseY;
    this._cachedState.mouseWorldX = 0; // Will be set by renderer based on camera
    this._cachedState.mouseWorldY = 0;
    this._cachedState.mouseDown = this.mouseDown;
    this._cachedState.rightMouseDown = this.rightMouseDown;
    this._cachedState.middleMouseDown = this.middleMouseDown;
    this._cachedState.mouseDeltaX = deltaX;
    this._cachedState.mouseDeltaY = deltaY;

    return this._cachedState;
  }

  isKeyDown(code: string): boolean {
    return this.keys.has(code);
  }

  private handleCancelKey = (event: KeyboardEvent): boolean => {
    if (event.code !== KeyBindings.CANCEL) {
      return false;
    }

    // ESC menu confirming -> cancel confirmation
    if (isEscMenuOpen.value && isRespawnConfirming.value) {
      cancelRespawnConfirmation();
      event.preventDefault();
      return true;
    }

    // ESC menu open -> close it
    if (isEscMenuOpen.value) {
      closeEscMenu();
      event.preventDefault();
      return true;
    }

    // Cancel construction placement
    if (isPlacingConstruction.value) {
      cancelPlacement();
      event.preventDefault();
      return true;
    }

    // Close construction panel
    if (isConstructionPanelOpen.value) {
      toggleConstructionPanel();
      event.preventDefault();
      return true;
    }

    // Nothing else to cancel -> open ESC menu
    openEscMenu();
    event.preventDefault();
    return true;
  };

  private handleFightModeKey = (event: KeyboardEvent): boolean => {
    if (event.code !== KeyBindings.FIGHT_MODE || isConstructionPanelOpen.value) {
      return false;
    }

    toggleFightMode();
    event.preventDefault();
    return true;
  };

  private handleConstructionPanelKey = (event: KeyboardEvent): boolean => {
    if (event.code !== KeyBindings.CONSTRUCTION_PANEL) {
      return false;
    }

    toggleConstructionPanel();
    event.preventDefault();
    return true;
  };

  private handleCategoryHotkey = (event: KeyboardEvent): boolean => {
    for (const [category, hotkey] of Object.entries(CATEGORY_HOTKEYS)) {
      if (event.code === `Key${hotkey}`) {
        setActiveCategory({
          category: category as (typeof ConstructionCategory)[keyof typeof ConstructionCategory],
        });
        event.preventDefault();
        return true;
      }
    }
    return false;
  };

  private handleConstructionItemHotkey = (event: KeyboardEvent): boolean => {
    const key = event.key;

    // Handle individual construction hotkeys (1-9)
    if (key >= '1' && key <= '9') {
      const handled = selectConstructionByHotkey({ hotkey: key });
      if (handled) {
        event.preventDefault();
        return true;
      }
    }

    // Handle letter hotkeys for construction
    if (key.length === 1 && LETTER_REGEX.test(key)) {
      const handled = selectConstructionByHotkey({ hotkey: key.toUpperCase() });
      if (handled) {
        event.preventDefault();
        return true;
      }
    }

    return false;
  };

  private handleConstructionHotkeys = (event: KeyboardEvent): boolean => {
    if (!isConstructionPanelOpen.value) {
      return false;
    }

    return this.handleCategoryHotkey(event) || this.handleConstructionItemHotkey(event);
  };

  private handleFullscreenKey = (event: KeyboardEvent): boolean => {
    if (event.code !== KeyBindings.FULLSCREEN) {
      return false;
    }

    event.preventDefault();
    this.toggleFullscreen();
    return true;
  };

  private handleDebugKeys = (event: KeyboardEvent): boolean => {
    if (event.code === KeyBindings.DEBUG_MENU) {
      const handled = handleDebugInput({ key: 'F3' });
      if (handled) {
        event.preventDefault();
      }
      return true;
    }

    const debugKey = DEBUG_DIGIT_BY_CODE[event.code];
    if (debugKey) {
      const handled = handleDebugInput({ key: debugKey });
      if (handled) {
        event.preventDefault();
      }
      return true;
    }

    return false;
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (this.isEditableTarget({ target: event.target })) {
      return;
    }

    if (this.isCameraControlKey({ code: event.code })) {
      // Avoid browser arrow-key scrolling/focus navigation while panning.
      event.preventDefault();
    }

    // Repeats can flood keydown events while movement keys are already held.
    if (event.repeat) {
      return;
    }

    this.keys.add(event.code);

    if (this.handleCancelKey(event)) {
      return;
    }

    // Suppress all other keys while ESC menu is open
    if (isEscMenuOpen.value) {
      event.preventDefault();
      return;
    }

    if (this.handleFightModeKey(event)) {
      return;
    }

    if (this.handleConstructionPanelKey(event)) {
      return;
    }

    if (this.handleConstructionHotkeys(event)) {
      return;
    }

    if (this.handleFullscreenKey(event)) {
      return;
    }

    this.handleDebugKeys(event);
  };

  private toggleFullscreen = (): void => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private isEditableTarget = ({ target }: { target: EventTarget | null }): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return true;
    }

    return target.isContentEditable;
  };

  private isCameraControlKey = ({ code }: { code: string }): boolean => {
    return (
      code === KeyBindings.CAMERA_UP ||
      code === KeyBindings.CAMERA_DOWN ||
      code === KeyBindings.CAMERA_LEFT ||
      code === KeyBindings.CAMERA_RIGHT ||
      code === KeyBindings.CAMERA_UP_ALT ||
      code === KeyBindings.CAMERA_DOWN_ALT ||
      code === KeyBindings.CAMERA_LEFT_ALT ||
      code === KeyBindings.CAMERA_RIGHT_ALT
    );
  };

  private handleMouseMove = (event: MouseEvent): void => {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  };

  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.mouseDown = true;
    } else if (event.button === 1) {
      this.middleMouseDown = true;
      event.preventDefault();
    } else if (event.button === 2) {
      this.rightMouseDown = true;
    }
  };

  private handleMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.mouseDown = false;
    } else if (event.button === 1) {
      this.middleMouseDown = false;
    } else if (event.button === 2) {
      this.rightMouseDown = false;
    }
  };

  private handleContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.wheelDeltaQueue.push({
      deltaY: event.deltaY,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };
}
